<script lang="ts">
	//Svelte
	import { beforeUpdate, onDestroy, onMount } from 'svelte';
	//Services
	import { format } from 'svelte-i18n';
	import { postReq } from '../../../services/http';
	//Stores
	import { devices } from 'src/stores/lists/devices';
	import { alarms } from 'src/stores/lists/scenes-derived';
	import { is_mobile } from 'src/stores/ui/viewport';
	import { edit } from 'src/stores/ui/edit_lists';
	//Helpers
	import { load_image_from } from '../../../helpers/interface_helpers';
	import {
		onStartDrag,
		onEndDrag,
		filterSensors,
		requestReorderDevice,
		getAllAlarmsGroupedDevices,
		checkIsAllAlarmsArmed,
		type AlarmDelayModalType,
		type AlarmDelayTimerType,
	} from '../list-helpers';
	import {
		alarm_object_prototype,
		ALARM_TIME_TRIGGER_DEFAULT,
		type IAlarmZone,
	} from '../../AlarmZones/AlarmWizard/alarm_object';
	import { capFirstChar, _deepClone } from '../../../helpers/utilities';
	//Design System Components
	import {
		CardGrid,
		Button,
		Toolbar,
		Breadcrumbs,
		IconButton,
		toast,
		Icon,
		BreadcrumbsFlexWrapper,
		Input,
	} from '@Shelly/shelly-design-system';
	//Components
	import AddEditButtons from '../../AddEditButtons.svelte';
	import EmptyList from '../EmptyList.svelte';
	import EmptyDeviceTypeList from '../EmptyDeviceTypeList.svelte';
	import AlarmWizard from '../../AlarmZones/AlarmWizard/AlarmWizard.svelte';
	import PinUnlock from 'src/components/PinUnlock.svelte';
	import EmptyPlaceholder from 'src/components/EmptyPlaceholder.svelte';
	import AlarmArmAllModal from './components/AlarmArmAllModal.svelte';
	import AlarmBreachedSensorModal from './components/AlarmBreachedSensorModal.svelte';
	import { showNewLayout } from 'src/stores/toDelete/new_layout';
	import { setSidePanelState } from 'src/components/Sidepanel/sidepanel-helpers';
	import { alarmMainFilter } from 'src/stores/ui/filters';
	import AlarmListContent from './AlarmListContent.svelte';
	import AlarmDeleteModal from './AlarmDeleteModal.svelte';
	import { clearAlarmItemNewFlag } from 'src/helpers/add-entity-helpers';
	import { isActiveHomeShared } from 'src/helpers/houses/homes_helpers';
	import { homes } from 'src/stores/account/home';
	import SharedHomeOwnerManagedModal from 'src/components/SharedHome/SharedHomeOwnerManagedModal.svelte';
	import {
		getSharedHomeEmptyListCopy,
		type SharedHomeManagedContext,
	} from 'src/helpers/houses/shared_home_messaging_helpers';

	export let list;
	export let type = 'alarms';
	// export let id;
	const REQUEST_TYPE = 'scene';
	const FLIP_DURATION_MS = 0;

	let alarm_id;
	let alarm_name = '';
	let turnType: boolean;

	let showAddEditAlarmModal = false;
	let showSharedManagedModal = false;
	let sharedManagedContext: SharedHomeManagedContext = 'add_alarm';
	let showDeleteAlarmModal = false;
	let dragDisabled = true;
	let currentRoute = '';
	let sensors = [];
	let alarm_object = _deepClone(alarm_object_prototype);
	let showPinLockModal = false;

	const handle_add_alarm = () => {
		if (isActiveHomeShared($homes.list)) {
			sharedManagedContext = 'add_alarm';
			showSharedManagedModal = true;
			return;
		}
		showAddEditAlarmModal = true;
		alarm_id = '';
		alarm_object = _deepClone(alarm_object_prototype);
	};
	let alarmsDelayModal: AlarmDelayModalType = {};
	let alarmsDelayTimer: AlarmDelayTimerType = {};
	let showArmingAllModal = false;
	let showBreachedModal = false;

	$: isEmptyDeviceList = Object.values($devices).length === 0;
	$: isEmptySensorsList = sensors.length === 0;
	$: items = $edit && currentRoute === type ? items : [...list];
	$: dndSettings = {
		items,
		flipDurationMs: FLIP_DURATION_MS,
		dragDisabled: dragDisabled,
	};

	function toggleAlarmsWithPin(items, enabled) {
		// this function check for situation when if we have for example two alarms. one alarm is with pin and it is enabled, the other is without pin and it is disabled.
		// if we want to enable all alarms in this case the pin modal should not appear, because the inactive(disabled) alarm doesn't have pin lock
		// but if we want to disable all alarms, the pin modal shoul appear, because the active(enabled) alarm has pin lock
		// enabled is 'true' if there is active alarm and should be inactivated, and is 'false' if there is inactive alarm and should be activated
		let filtratedItem = items.filter((item) => item.pin_lock && item._enabled === enabled);
		return filtratedItem.length > 0;
	}

	onMount(async () => {
		currentRoute = type;

		sensors = [...filterSensors(Object.values($devices))];
	});

	onDestroy(() => {
		edit.set(false);
		/*
		 * Intentionally clears `_meta.new` for every alarm in the scenes store (global), not
		 * room-scoped like `clearGroupsItemNewFlag` / `clearScenesItemNewFlag` / `clearThermostatsItemNewFlag`.
		 * Leaving the Alarms screen should not leave stale pulse state; alarms are not split by room here.
		 */
		clearAlarmItemNewFlag();
	});

	beforeUpdate(() => {
		if (sensors.length === 0) {
			sensors = [...filterSensors(Object.values($devices))];
		}

		if (!$edit) handleEndDrag();

		if (currentRoute !== type) {
			currentRoute = type;
		}
	});

	const onCardClick = (item) => {
		if ($edit) return;

		setSidePanelState({ type: 'alarm', id: item._id, open: true });
	};

	const onCardEdit = async (event: MouseEvent | TouchEvent, item) => {
		event.stopPropagation();
		alarm_id = item._id;

		alarm_name = item?._meta?.name || 'unknown';
		alarm_object = _deepClone($alarms[alarm_id]);
		showAddEditAlarmModal = true;
	};

	const onCardDelete = (event: MouseEvent | TouchEvent, item) => {
		event.stopPropagation();
		alarm_id = item._id;

		alarm_name = item?._meta?.name || 'unknown';
		showDeleteAlarmModal = true;
	};

	// DND Functions
	const handleStartDrag = () => {
		dragDisabled = false;
	};

	const handleEndDrag = () => {
		dragDisabled = true;
	};

	const handleReorder = (e: CustomEvent) => {
		items = e.detail.items;
	};

	const handleReorderEnd = async (e: CustomEvent) => {
		let editedList = {};
		e.detail.items.map((v, i) => {
			delete v?.isDndShadowItem;

			editedList[v.id] = {
				...v,
				_meta: {
					...v._meta,
					position: i,
				},
			};
		});

		if (Object.keys(editedList).length === 0) return;

		items = [...e.detail.items];
		handleEndDrag();

		await requestReorderDevice(REQUEST_TYPE, editedList);
	};

	const handleLocked = () => {
		let alarmHasPin = toggleAlarmsWithPin(items, false);
		if (alarmHasPin) {
			showPinLockModal = true;
			turnType = true;
		}
		if (!alarmHasPin) {
			handleAllAlarms(true);
		}
	};

	const turnOnAll = () => {
		const alarmGroupedDevices = getAllAlarmsGroupedDevices(items);
		const isAllArmed = checkIsAllAlarmsArmed(items, false);
		if (
			(alarmGroupedDevices?.breached?.length || alarmGroupedDevices?.offline?.length) &&
			!isAllArmed
		) {
			showBreachedModal = true;
		} else {
			handleLocked();
		}
	};

	const turnOffAll = () => {
		let alarmHasPin = toggleAlarmsWithPin(items, true);
		if (alarmHasPin) {
			showPinLockModal = true;
			turnType = false;
		}

		if (!alarmHasPin) {
			handleAllAlarms(false);
		}
	};

	const handleAllAlarms = async (isEnabled: boolean) => {
		try {
			showArmingAllModal = isEnabled;
			const alarmIdsEditList = Object.values($alarms)
				.map((alarm: IAlarmZone) => {
					if (!alarm._enabled === isEnabled) {
						return alarm._id;
					}
				})
				.filter((alarmID) => alarmID);

			if (!alarmIdsEditList.length) return;

			const res = await postReq('scene/bulk_meta_update', {
				ids: JSON.stringify(alarmIdsEditList),
				enabled: isEnabled ? 1 : 0,
				meta: JSON.stringify({
					_time_trigger: isEnabled ? Date.now() : ALARM_TIME_TRIGGER_DEFAULT,
				}),
			});

			if (res.isok) {
				//TODO UPDATE STORE WITH BULK UPDATE. ASK TO UPDATE RES
				// scenes.bulkEditScenes(res.data.scene_script);
				if (!isEnabled) {
					toast.open({
						type: 'info',
						title: $format('info_arm_disarm_alarms', {
							values: {
								value: $format(`alarms_${isEnabled ? 'armed' : 'disarmed'}`),
							},
						}),
					});
				}

				turnType = undefined;
			} else {
				toast.open({
					type: 'danger',
					title: $format('error_global'),
				});
			}
		} catch (error) {
			toast.open({
				type: 'danger',
				title: $format('error_global'),
			});
			turnType = undefined;
		}
	};

	function handleClose() {
		showPinLockModal = false;
	}
</script>

<Toolbar
	class="mb-m"
	isSticky
	useNewLayout={$showNewLayout}
	hideSlotRightSide={$showNewLayout}
	hideSlotLeftSide={!$showNewLayout || !$is_mobile}
>
	<svelte:fragment slot="breadcrumbs">
		{#if !$showNewLayout}
			<Breadcrumbs
				aria-label={$format('label_alarms', { default: 'Alarms' })}
				links={[{ label: capFirstChar($format('tab_alarms')) }]}
			/>
		{:else}
			<BreadcrumbsFlexWrapper gap="s">
				<div
					class="d-flex gap-s align-items-center flex-1 min-width-0 max-width-fit-content"
					slot="left"
				>
					<Breadcrumbs
						class="flex-1 min-width-0"
						listClasses="width-full"
						aria-label={$format('label_alarms', { default: 'Alarms' })}
						links={[{ label: capFirstChar($format('tab_alarms')) }]}
					/>

					{#if !$is_mobile && !$edit}
						<Input
							bind:value={$alarmMainFilter}
							class="width-md-l mr-sm-s text-capitalize-placeholder"
							type="text"
							label={capFirstChar($format('search', { default: 'Search' }))}
							icon="fas fa-search"
							id="alarms-toolbar-search-input"
							aria-labelledby="alarms-toolbar-search-input"
						/>
					{/if}
				</div>
				<div slot="right">
					{#if !$edit}
						<IconButton
							class="mr-s"
							aria-label={$format('button_arm_all_alarms', {
								default: 'Button arm all alarms',
							})}
							variant="secondary"
							icon="fas fa-shield text-success"
							on:click={turnOnAll}
						/>
						<IconButton
							class="mr-s"
							aria-label={$format('button_disarm_all_alarms', {
								default: 'Button disarm all alarms',
							})}
							variant="secondary"
							icon="fas fa-shield text-danger"
							on:click={turnOffAll}
						/>
					{/if}
					{#if !$is_mobile}
						<Button
							size="m"
							variant="secondary"
							class="align-self-center"
							disabled={items.length <= 1}
							icon={$edit ? '' : 'fa-solid fa-arrows-up-down-left-right'}
							on:click={() => {
								edit.set(!$edit);
							}}
						>
							<!-- TODO: Add new translation -->
							{$edit
								? $format('label_finish_reordering', {
										default: 'Finish reordering',
									})
								: $format('label_reorder_items', { default: 'Reorder items' })}
						</Button>
					{/if}
				</div>
			</BreadcrumbsFlexWrapper>
		{/if}
	</svelte:fragment>

	<svelte:fragment slot="rightSide">
		<IconButton
			aria-label={$format('button_arm_all_alarms', {
				default: 'Button arm all alarms',
			})}
			variant="secondary"
			icon="fas fa-shield text-success"
			on:click={turnOnAll}
		/>
		<IconButton
			aria-label={$format('button_disarm_all_alarms', {
				default: 'Button disarm all alarms',
			})}
			variant="secondary"
			icon="fas fa-shield text-danger"
			on:click={turnOffAll}
		/>

		<AddEditButtons
			disableEditButton={items.length <= 0}
			disableAddButton={isEmptyDeviceList || isEmptySensorsList}
			interceptEditClick={isActiveHomeShared($homes.list)}
			on:add_click={() => handle_add_alarm()}
			on:edit_click={() => {
				sharedManagedContext = 'edit_entity';
				showSharedManagedModal = true;
			}}
			hideAddButton={$showNewLayout}
		/>
	</svelte:fragment>

	<svelte:fragment slot="leftSide">
		<div class="d-flex flex-direction-column align-items-center gap-s justify-content-center">
			{#if !$edit}
				<Input
					bind:value={$alarmMainFilter}
					class="text-capitalize-placeholder"
					type="text"
					label={capFirstChar($format('search', { default: 'Search' }))}
					icon="fas fa-search"
					id="alarms-toolbar-search-input"
					aria-labelledby="alarms-toolbar-search-input"
				/>
			{/if}
			<Button
				size="m"
				variant="secondary"
				class="align-self-center width-full"
				icon={$edit ? '' : 'fa-solid fa-arrows-up-down-left-right'}
				disabled={items.length <= 1}
				on:click={() => {
					edit.set(!$edit);
				}}
			>
				<!-- TODO: Add new translation -->
				{$edit
					? $format('label_finish_reordering', {
							default: 'Finish reordering',
						})
					: $format('label_reorder_items', { default: 'Reorder items' })}
			</Button>
		</div>
	</svelte:fragment>
</Toolbar>

<!-- {#if isEmptyDeviceList && Object.values($alarms).length <= 0}
	<EmptyDeviceList />
	{/if} -->
{#if items.length <= 0 && !!$alarmMainFilter}
	<div class="mt-md-m mt-sm-0 text-center d-flex flex-direction-column align-items-center">
		<Icon size="l" icon="fal fa-magnifying-glass" class="mt-md-l mt-sm-s mb-m" />
		<EmptyList
			title={$format('no_such_alarm_found', { default: 'No such alarms found.' })}
			description={$format('check_the_spelling_or_try_new_search', {
				default: 'Check the spelling or try a new search.',
			})}
		/>
	</div>
{:else if items.length <= 0 && isActiveHomeShared($homes.list)}
	{@const sharedEmptyCopy = getSharedHomeEmptyListCopy('alarms')}
	<div class="mt-md-m mt-sm-0 text-center d-flex flex-direction-column">
		<EmptyPlaceholder class="mt-md-l mt-sm-s mb-m" />
		<EmptyList
			title={$format(sharedEmptyCopy.titleKey, { default: sharedEmptyCopy.titleDefault })}
			description={$format(sharedEmptyCopy.descrKey, {
				default: sharedEmptyCopy.descrDefault,
			})}
		/>
	</div>
{:else if isEmptySensorsList && Object.values($alarms).length <= 0}
	<div class="mt-md-m mt-sm-0 text-center d-flex flex-direction-column">
		<EmptyPlaceholder class="mt-md-l mt-sm-s mb-m" />
		<EmptyDeviceTypeList />
	</div>
{:else if items.length <= 0}
	<div class="mt-md-m mt-sm-0 text-center d-flex flex-direction-column">
		<EmptyPlaceholder class="mt-md-l mt-sm-s mb-m" />
		<EmptyList
			title={$format('no_alarms_title', {
				default: 'There are no alarms yet',
			})}
			description={$format('no_alarms_descr', {
				default: 'Try creating an Alarm for better safety and alertness.',
			})}
			btnText={$format('button_add_alarm', {
				default: 'Add alarm',
			})}
			onClick={() => handle_add_alarm()}
		/>
	</div>
{:else}
	<CardGrid
		editMode={$edit}
		xl={350}
		lg={290}
		sm={270}
		{dndSettings}
		on:consider={handleReorder}
		on:finalize={handleReorderEnd}
	>
		{#each items as item, _ (item)}
			<div
				class:dnd-placeholder={item.isDndShadowItem}
				tabindex="0"
				role="button"
				aria-label={$format('button_drag_alarm_to_reorder', {
					default: 'Drag alarm to reorder',
				})}
			>
				<AlarmListContent
					{item}
					{load_image_from}
					{onCardClick}
					{onCardDelete}
					{onCardEdit}
					{alarmsDelayModal}
					{alarmsDelayTimer}
					on:handle-drag-start={(e) => onStartDrag(e, $edit, handleStartDrag, true)}
					on:handle-drag-end={(e) => onEndDrag(e, $edit, handleEndDrag, true)}
					on:mousedown={(e) => onStartDrag(e, $edit, handleStartDrag)}
					on:touchstart={(e) => onStartDrag(e, $edit, handleStartDrag)}
					on:mouseup={(e) => onEndDrag(e, $edit, handleEndDrag)}
					on:touchend={(e) => onEndDrag(e, $edit, handleEndDrag)}
				/>
			</div>
		{/each}
	</CardGrid>
{/if}

{#if showAddEditAlarmModal}
	<AlarmWizard bind:id={alarm_id} bind:showAddEditAlarmModal {alarm_object} />
{/if}

<AlarmDeleteModal bind:showDeleteAlarmModal bind:items {alarm_id} {alarm_name} />

<SharedHomeOwnerManagedModal bind:showModal={showSharedManagedModal} context={sharedManagedContext} />

{#if showPinLockModal}
	<PinUnlock
		bind:showModal={showPinLockModal}
		on:close={handleClose}
		on:unlock={() => handleAllAlarms(turnType)}
		on:cancel={() => (showPinLockModal = false)}
	/>
{/if}

{#if !showPinLockModal}
	<AlarmArmAllModal bind:showModal={showArmingAllModal} {alarmsDelayTimer} />
{/if}

{#if showBreachedModal}
	<AlarmBreachedSensorModal
		alarm={undefined}
		alarmsList={items}
		showItemsSubtitle={true}
		bind:showModal={showBreachedModal}
		on:armAlarm={handleLocked}
	/>
{/if}
